import React, { useState, useEffect } from 'react'
import {
  CheckCircle,
  XCircle,
  Clock,
  Trophy,
  Target,
  RotateCcw,
  BookOpen,
  TrendingUp,
  BarChart3,
  Lightbulb,
  AlertTriangle,
  AlertCircle,
  Brain,
  Sparkles
} from 'lucide-react'
import { useStudyStore } from '../../store/useStudyStore'
import { QuizScoringService, ScoringResult, QuestionAnalysis } from '../../services/quiz/QuizScoringService'
import { aiQuizAnalysisService, AIQuizAnalysis } from '../../services/ai/AIQuizAnalysisService'
// NEW: Sử dụng PHP Email Service thay thế EmailJS
import { emailServicePHP } from '../../services/email/EmailServicePHP'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'
import KaTeXRenderer from '../common/KaTeXRenderer'
import TrueFalseQuestion from './TrueFalseQuestion'
import MultipleChoiceReview from './MultipleChoiceReview'
import SolutionDisplay from './SolutionDisplay'
import TrueFalseSolutionDisplay from './TrueFalseSolutionDisplay'
import MultipleChoiceSolutionDisplay from './MultipleChoiceSolutionDisplay'
import ShortAnswerSolutionDisplay from './ShortAnswerSolutionDisplay'
import { TLUSolutionDisplay } from './TLUSolutionDisplay'
import { Achievement } from '../../types/learningHistory'

interface QuizAnswer {
  questionId: string
  userAnswer: any
  isCorrect: boolean
  timeSpent: number
}

interface QuizSubmissionProps {
  quizId: string
  lessonId: string
  questions: any[]
  answers: Record<string, any>
  timeSpent: number
  quizData: any
  onRetry: () => void
  onContinue: () => void
  initialResults?: ScoringResult
  initialAchievements?: Achievement[]
}

const QuizSubmission: React.FC<QuizSubmissionProps> = ({
  quizId,
  lessonId,
  questions,
  answers,
  timeSpent,
  quizData,
  onRetry,
  onContinue,
  initialResults,
  initialAchievements
}) => {
  const [results, setResults] = useState<ScoringResult | null>(initialResults || null)
  const [isSubmitting, setIsSubmitting] = useState(!initialResults)
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(true)
  const [aiAnalysis, setAiAnalysis] = useState<AIQuizAnalysis | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [showAIAnalysis, setShowAIAnalysis] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [achievements, setAchievements] = useState<Achievement[]>(initialAchievements || [])
  const { submitQuiz, updateLessonProgress } = useStudyStore()
  const { user } = useAuth()

  useEffect(() => {
    console.log('🔄 QuizSubmission useEffect triggered', {
      hasInitialResults: !!initialResults,
      hasUser: !!user,
      aiAnalysis: !!aiAnalysis,
      isAnalyzing
    })

    if (!initialResults) {
      console.log('⚡ No initial results, calling handleSubmitQuiz...')
      handleSubmitQuiz()
    } else {
      if (initialResults.passed) {
        setShowConfetti(true)
      }
      // ✅ Auto-trigger AI analysis (now works for guests too)
      if (!aiAnalysis && !isAnalyzing) {
        console.log('🚀 Auto-triggering AI analysis from useEffect...')
        handleAIAnalysis(initialResults)
      } else {
        console.log('⚠️ Skipping auto-trigger:', { hasAnalysis: !!aiAnalysis, isAnalyzing })
      }
    }
  }, [initialResults])

  const handleSubmitQuiz = async () => {
    setIsSubmitting(true)

    try {
      console.log('🎯 Starting quiz submission with improved scoring...')

      // Sử dụng QuizScoringService mới để chấm điểm
      const scoringResult = QuizScoringService.scoreQuiz(
        questions,
        answers,
        timeSpent,
        quizData?.diem_dat || quizData?.metadata?.diemDat || 70
      )

      setResults(scoringResult)

      // GỬI EMAIL NGAY SAU KHI CHẤM ĐIỂM - Không phụ thuộc vào việc lưu database
      console.log('🎯 Bắt đầu gửi email thông báo kết quả...')
      await sendQuizResultEmails(scoringResult)

      // ✅ FIX: Submit quiz result với đầy đủ thông tin từ scoringResult
      try {
        // Tính correctAnswers và wrongAnswers từ scoringResult.answers
        const correctAnswers = scoringResult.answers.filter(answer => answer.isCorrect).length
        const wrongAnswers = scoringResult.answers.filter(answer => !answer.isCorrect).length
        const totalQuestions = scoringResult.answers.length

        // ✅ Sử dụng quizHistoryService để lưu với đầy đủ thông tin
        if (user?.id) {
          const { quizHistoryService } = await import('../../services/studyHistory/QuizHistoryService')

          await quizHistoryService.saveQuizResult({
            userId: user.id,
            quizId: quizId,
            lessonId: lessonId,
            quizTitle: quizData?.ten || quizData?.tieu_de || 'Bài trắc nghiệm',
            quizType: 'lesson',
            score: parseFloat(scoringResult.score.toFixed(2)), // ✅ Đảm bảo 2 chữ số thập phân
            totalQuestions: totalQuestions,
            correctAnswers: correctAnswers, // ✅ Số câu đúng
            wrongAnswers: wrongAnswers, // ✅ Số câu sai
            skippedAnswers: 0,
            timeSpent: timeSpent,
            passed: scoringResult.passed,
            passingScore: quizData?.diem_dat || quizData?.metadata?.diemDat || 70,
            answers: scoringResult.answers.map((answer, index) => {
              // Tìm question tương ứng để lấy questionText và questionType
              const question = questions.find(q => q.id === answer.questionId) || questions[index]
              const rawQuestionType = question?.type || question?.loai || answer.questionType || 'multiple_choice'

              // ✅ Map questionType sang format 'TN' | 'DS' | 'TL'
              let questionType: 'TN' | 'DS' | 'TL' = 'TN'
              if (rawQuestionType === 'dung_sai' || rawQuestionType === 'true_false') {
                questionType = 'DS'
              } else if (rawQuestionType === 'tinh_toan' || rawQuestionType === 'short_answer' || rawQuestionType === 'tra_loi_ngan') {
                questionType = 'TL'
              } else {
                questionType = 'TN' // Mặc định là TN
              }

              return {
                questionId: answer.questionId,
                questionText: question?.cau_hoi || question?.question || '',
                questionType: questionType,
                userAnswer: answer.userAnswer,
                correctAnswer: answer.correctAnswer,
                isCorrect: answer.isCorrect,
                timeSpent: answer.timeSpent,
                explanation: answer.explanation
              }
            }),
            metadata: {
              percentage: parseFloat(scoringResult.percentage.toFixed(2)), // ✅ Đảm bảo 2 chữ số thập phân
              maxScore: parseFloat(scoringResult.maxScore.toFixed(2))
            }
          })

          console.log(`✅ Quiz result saved: Score ${scoringResult.score.toFixed(2)}/${scoringResult.maxScore.toFixed(2)}, ${correctAnswers}/${totalQuestions} correct`)
        }

        // Update lesson progress
        await updateLessonProgress(lessonId, 100, timeSpent)

        toast.success('Đã lưu kết quả bài thi!')
      } catch (error) {
        console.error('Error submitting quiz:', error)
        // Không hiển thị lỗi vì email đã gửi thành công
      }

      // ✅ Auto-trigger AI analysis (now works for guests too)
      handleAIAnalysis(scoringResult)

    } catch (error) {
      console.error('Error in quiz submission:', error)
      toast.error('Có lỗi xảy ra khi chấm điểm bài thi')
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Gửi email thông báo kết quả quiz cho user và admin
   */
  const sendQuizResultEmails = async (scoringResult: ScoringResult) => {
    try {
      console.log('📧 Preparing to send quiz result emails via PHP API...')
      console.log('👤 USER INFO:', {
        name: user?.name,
        email: user?.email,
        hasEmail: !!user?.email
      })

      // Tính toán metrics
      const correctCount = scoringResult.answers.filter(answer => answer.isCorrect).length
      const totalQuestions = scoringResult.answers.length
      const durationSeconds = Math.round(timeSpent)

      // Gửi email qua PHP API
      const emailResult = await emailServicePHP.notifyLessonCompletion(
        user?.name || 'Học viên',
        user?.email,
        quizData?.ten || 'Bài kiểm tra',
        lessonId,
        correctCount,
        totalQuestions,
        durationSeconds
      )

      if (emailResult.success) {
        console.log('✅ Email sent successfully:', emailResult)
        toast.success('📧 Đã gửi thông báo kết quả qua email')
      } else {
        console.warn('⚠️ Email failed:', emailResult.message)
        // Không hiển thị lỗi cho user vì email không phải tính năng quan trọng
      }

      return // Exit early

      // console.log('📧 Preparing to send quiz result emails...')

      // Chuẩn bị dữ liệu email
      // const emailData: QuizEmailData = {
      //   quizId: quizId,
      //   quizTitle: quizData?.ten || 'Bài kiểm tra',
      //   lessonId: lessonId,
      //   userName: user?.name || 'Học viên',
      //   userEmail: user?.email || 'guest@example.com',
      //   score: scoringResult.score,
      //   maxScore: scoringResult.maxScore,
      //   percentage: scoringResult.percentage,
      //   correctAnswers: scoringResult.answers.filter(answer => answer.isCorrect).length,
      //   totalQuestions: scoringResult.answers.length,
      //   timeSpent: timeSpent,
      //   passed: scoringResult.passed,
      //   completedAt: new Date(),
      //   feedback: scoringResult.feedback
      // }

      // Gửi email cho user
      // DISABLED: EmailJS đã hết quota free
      // console.log('📧 EmailJS disabled - Email notification skipped')
      // const userEmailResult = await emailServiceWithEmailJS.sendUserQuizResultEmail(emailData)
      // if (userEmailResult.success) {
      //   console.log('✅ User email sent:', userEmailResult.message)
      // } else {
      //   console.warn('⚠️ User email failed:', userEmailResult.message)
      // }

      // Gửi email cho admin
      // DISABLED: EmailJS đã hết quota free
      // const adminEmailResult = await emailServiceWithEmailJS.sendAdminQuizResultEmail(emailData)
      // if (adminEmailResult.success) {
      //   console.log('✅ Admin email sent:', adminEmailResult.message)
      // } else {
      //   console.warn('⚠️ Admin email failed:', adminEmailResult.message)
      // }

      // Hiển thị thông báo tổng hợp
      // DISABLED: EmailJS đã hết quota free
      // if (userEmailResult.success && adminEmailResult.success) {
      //   toast.success('📧 Đã gửi email thông báo kết quả')
      // }

    } catch (error) {
      console.error('❌ Error sending emails:', error)
      // Không hiển thị lỗi cho user vì email không phải tính năng quan trọng
    }
  }

  const handleAIAnalysis = async (scoringResult?: ScoringResult) => {
    console.log('🤖 handleAIAnalysis called', {
      hasScoringResult: !!scoringResult,
      hasCurrentResults: !!results,
      user: user?.id
    })

    const resultToAnalyze = scoringResult || results

    if (!resultToAnalyze) {
      console.error('❌ No result to analyze')
      toast.error('Không có kết quả để phân tích')
      return
    }

    // if (!user) {
    //   toast.error('Bạn cần đăng nhập để sử dụng phân tích AI.')
    //   return
    // }

    setIsAnalyzing(true)
    setShowAIAnalysis(true)

    try {
      console.log('🤖 Starting AI analysis...')

      const analysis = await aiQuizAnalysisService.analyzeQuizResults(
        resultToAnalyze,
        questions,
        lessonId,
        { userId: user?.id } // Pass userId if available, otherwise undefined
      )

      setAiAnalysis(analysis)

      if (analysis.success) {
        toast.success('Phân tích AI hoàn tất!')
      } else if (analysis.error) {
        toast.error(analysis.error)
      } else {
        toast.success('Đã tạo phân tích cơ bản')
      }

    } catch (error) {
      console.error('Error in AI analysis:', error)
      toast.error('Có lỗi xảy ra khi phân tích bằng AI')
    } finally {
      setIsAnalyzing(false)
    }
  }

  if (!results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              {isSubmitting ? 'Đang chấm điểm bài thi...' : 'Chuẩn bị kết quả...'}
            </h2>
            <p className="text-gray-500">Vui lòng đợi trong giây lát</p>
          </div>
        </div>
      </div>
    )
  }

  const { strengthAreas, weakAreas, recommendations } = QuizScoringService.analyzeResults(results.detailedAnalysis)
  const showScore = quizData?.settings?.show_score ?? true;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="text-center mb-6">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${results.passed ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
              }`}>
              {results.passed ? (
                <Trophy className="w-10 h-10" />
              ) : (
                <Target className="w-10 h-10" />
              )}
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {showScore ? (results.passed ? '🎉 Chúc mừng!' : '📚 Cần cải thiện!') : '🎉 Đã nộp bài thành công!'}
            </h1>
            <p className="text-lg text-gray-600">
              {showScore ? results.feedback : 'Kết quả chi tiết không được công bố theo cài đặt của đề thi.'}
            </p>
          </div>

          {/* Score Summary */}
          {showScore && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{results.score}</div>
                <div className="text-sm text-blue-500">Điểm đạt được</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-gray-600">{results.maxScore}</div>
                <div className="text-sm text-gray-500">Tổng điểm</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{results.percentage}%</div>
                <div className="text-sm text-green-500">Tỷ lệ đúng</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{Math.floor(timeSpent / 60)}:{String(timeSpent % 60).padStart(2, '0')}</div>
                <div className="text-sm text-purple-500">Thời gian làm bài</div>
              </div>
            </div>
          )}

          {/* Section Scores */}
          {showScore && results.sectionScores && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-blue-100 rounded-lg p-3 text-center shadow-sm">
                <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Phần I - Trắc nghiệm</div>
                <div className="text-lg font-bold text-blue-600">
                  {results.sectionScores.TN.score}/{results.sectionScores.TN.maxScore}
                </div>
                <div className="text-xs text-gray-400">({results.sectionScores.TN.count} câu)</div>
              </div>
              <div className="bg-white border border-purple-100 rounded-lg p-3 text-center shadow-sm">
                <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Phần II - Đúng/Sai</div>
                <div className="text-lg font-bold text-purple-600">
                  {results.sectionScores.DS.score}/{results.sectionScores.DS.maxScore}
                </div>
                <div className="text-xs text-gray-400">({results.sectionScores.DS.count} câu)</div>
              </div>
              <div className="bg-white border border-orange-100 rounded-lg p-3 text-center shadow-sm">
                <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Phần III - Trả lời ngắn</div>
                <div className="text-lg font-bold text-orange-600">
                  {results.sectionScores.TL.score}/{results.sectionScores.TL.maxScore}
                </div>
                <div className="text-xs text-gray-400">({results.sectionScores.TL.count} câu)</div>
              </div>
              <div className="bg-white border border-pink-100 rounded-lg p-3 text-center shadow-sm">
                <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Phần IV - Tự luận</div>
                <div className="text-lg font-bold text-pink-600">
                  {results.sectionScores.TLU.score}/{results.sectionScores.TLU.maxScore}
                </div>
                <div className="text-xs text-gray-400">({results.sectionScores.TLU.count} câu)</div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* Ẩn nút "Làm lại" - user có thể quay về bài học và làm lại từ đó */}
            {/* <button
              onClick={onRetry}
              className="btn-primary flex items-center justify-center gap-2 px-6 py-3 rounded-lg"
            >
              <RotateCcw className="w-5 h-5" />
              Làm lại bài thi
            </button> */}
            <button
              onClick={onContinue}
              className="btn-primary flex items-center justify-center gap-2 px-6 py-3 rounded-lg"
            >
              <BookOpen className="w-5 h-5" />
              Quay về bài học
            </button>
            <button
              onClick={() => {
                if (!showAIAnalysis) {
                  // Lần đầu click → Gọi AI analysis
                  handleAIAnalysis()
                } else {
                  // Đã phân tích → Toggle show/hide
                  setShowAIAnalysis(!showAIAnalysis)
                }
              }}
              className={`bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center justify-center gap-2 px-6 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl ${!showScore ? 'opacity-50 cursor-not-allowed hidden' : ''}`}
              disabled={isAnalyzing || !showScore}
            >
              {isAnalyzing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Đang phân tích AI...
                </>
              ) : (
                <>
                  <Brain className="w-5 h-5" />
                  {showAIAnalysis ? 'Ẩn phân tích AI' : '🤖 Phân tích AI'}
                </>
              )}
            </button>
            <button
              onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
              className={`btn-soft flex items-center justify-center gap-2 px-6 py-3 rounded-lg`}
            >
              <BarChart3 className="w-5 h-5" />
              {showDetailedAnalysis ? 'Ẩn chi tiết câu hỏi' : 'Xem chi tiết câu hỏi'}
            </button>
          </div>
        </div>

        {/* AI Analysis Section - Compact */}
        {showScore && showAIAnalysis && (
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg shadow-lg p-6 mb-6 border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-purple-900 flex items-center gap-2">
                <Brain className="w-6 h-6" />
                Phân tích AI
                <Sparkles className="w-4 h-4 text-pink-500" />
              </h2>
              {aiAnalysis?.tokensUsed && (
                <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                  {aiAnalysis.tokensUsed} tokens
                </span>
              )}
            </div>

            {/* Offline Mode Warning */}
            {aiAnalysis?.isFallback && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      Đang ở chế độ phân tích cơ bản (Offline).
                      <span className="block mt-1 text-xs">
                        Vui lòng cấu hình VITE_OPENAI_API_KEY hoặc Proxy để kích hoạt AI thông minh.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isAnalyzing ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-3"></div>
                <p className="text-purple-700 font-medium">Đang phân tích kết quả bằng AI...</p>
              </div>
            ) : aiAnalysis ? (
              <div className="space-y-4">
                {/* Overall Feedback - Compact */}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-200">
                  <h3 className="text-base font-semibold text-purple-900 mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-pink-500" />
                    Nhận xét AI
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed">{aiAnalysis.overallFeedback}</p>
                </div>

                {/* Strengths and Weaknesses - Full Interface */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Strengths - Full Interface */}
                  {aiAnalysis.strengths && aiAnalysis.strengths.length > 0 && (
                    <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                      <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Điểm mạnh
                      </h3>
                      <div className="space-y-4">
                        {aiAnalysis.strengths.map((strength, index) => (
                          <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-green-200">
                            <h4 className="font-medium text-green-900 mb-2 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              {strength.title}
                            </h4>
                            <p className="text-sm text-green-700 mb-3 leading-relaxed">{strength.description}</p>
                            {strength.examples && strength.examples.length > 0 && (
                              <div className="bg-green-100 rounded-md p-3 border-l-4 border-green-500">
                                <div className="text-xs text-green-800 font-medium mb-2">
                                  Ví dụ cụ thể:
                                </div>
                                <ul className="space-y-1">
                                  {strength.examples.map((example, i) => (
                                    <li key={i} className="text-sm text-green-700 flex items-center gap-2">
                                      <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                                      {example}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Weaknesses - Full Interface */}
                  {aiAnalysis.weaknesses && aiAnalysis.weaknesses.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                      <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Điểm cần cải thiện
                      </h3>
                      <div className="space-y-4">
                        {aiAnalysis.weaknesses.map((weakness, index) => (
                          <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-red-200">
                            <h4 className="font-medium text-red-900 mb-2 flex items-center gap-2">
                              <AlertCircle className="w-4 h-4 text-red-600" />
                              {weakness.title}
                            </h4>
                            <p className="text-sm text-red-700 mb-3 leading-relaxed">{weakness.description}</p>
                            {weakness.affectedQuestions && weakness.affectedQuestions.length > 0 && (
                              <div className="bg-red-100 rounded-md p-3 border-l-4 border-red-500">
                                <div className="text-xs text-red-800 font-medium mb-1">
                                  Câu bị ảnh hưởng:
                                </div>
                                <div className="text-sm text-red-700">
                                  {weakness.affectedQuestions.join(', ')}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Key Recommendations - Compact */}
                {(aiAnalysis.theoryRecommendations && aiAnalysis.theoryRecommendations.length > 0) && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      Nội dung cần học
                    </h3>
                    <div className="space-y-2">
                      {aiAnalysis.theoryRecommendations.slice(0, 3).map((rec, index) => (
                        <div key={index} className="text-sm text-blue-700">
                          <span className="font-medium">{rec.topic}:</span> {rec.reason}
                        </div>
                      ))}
                      {aiAnalysis.theoryRecommendations.length > 3 && (
                        <div className="text-xs text-blue-600 italic">
                          +{aiAnalysis.theoryRecommendations.length - 3} đề xuất khác...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 text-purple-600">
                <Brain className="w-12 h-12 mx-auto mb-3 text-purple-400" />
                <p className="text-sm">Nhấn nút "🤖 Phân tích AI" để xem phân tích chi tiết</p>
              </div>
            )}
          </div>
        )}

        {/* Detailed Analysis */}
        {showDetailedAnalysis && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              Phân tích chi tiết kết quả
            </h2>

            {/* Performance by Question Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Điểm mạnh
                </h3>
                <ul className="space-y-2">
                  {strengthAreas.map((area, index) => (
                    <li key={index} className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      {area}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-red-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Cần cải thiện
                </h3>
                <ul className="space-y-2">
                  {weakAreas.map((area, index) => (
                    <li key={index} className="flex items-center gap-2 text-red-700">
                      <XCircle className="w-4 h-4" />
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5" />
                Đề xuất cải thiện
              </h3>
              <ul className="space-y-2">
                {recommendations.map((rec, index) => (
                  <li key={index} className="flex items-center gap-2 text-blue-700">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>

            {/* Question-by-Question Analysis */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Chi tiết từng câu hỏi</h3>
              {results.detailedAnalysis.map((analysis, index) => {
                const question = questions[index]
                const questionType = question?.loai || question?.type
                const isTrueFalse = questionType === 'dung_sai' || questionType === 'true_false'
                const isMultipleChoice = questionType === 'trac_nghiem_nhieu_lua_chon' || questionType === 'multiple_choice'
                const isTLU = questionType === 'tlu' || (question.examMetadata?.group === 'TLU')

                return (
                  <div
                    key={analysis.questionId}
                    className={`border rounded-lg p-4 ${analysis.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                      }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-700">Câu {index + 1}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${analysis.difficulty === 'M1' ? 'bg-green-100 text-green-800' :
                          analysis.difficulty === 'M2' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                          {analysis.difficulty}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${analysis.questionType === 'trac_nghiem_nhieu_lua_chon' ? 'bg-blue-100 text-blue-800' :
                          analysis.questionType === 'dung_sai' ? 'bg-purple-100 text-purple-800' :
                            isTLU ? 'bg-pink-100 text-pink-800' :
                              'bg-orange-100 text-orange-800'
                          }`}>
                          {analysis.questionType === 'trac_nghiem_nhieu_lua_chon' ? 'Trắc nghiệm' :
                            analysis.questionType === 'dung_sai' ? 'Đúng/Sai' :
                              isTLU ? 'Tự luận' : 'Tính toán'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Show "Chưa chọn đáp án" for unanswered multiple choice questions */}
                        {isMultipleChoice && !analysis.userAnswer && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            Chưa chọn đáp án
                          </span>
                        )}
                        {analysis.isCorrect ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm font-medium text-gray-600">
                          {analysis.points}/{questions[index]?.diem || 1} điểm
                        </span>
                      </div>
                    </div>

                    {/* Enhanced Multiple Choice Question Display with Detailed Solution */}
                    {isMultipleChoice ? (
                      <div className="space-y-4">
                        <MultipleChoiceSolutionDisplay
                          question={question}
                          userAnswer={analysis.userAnswer}
                          analysis={analysis}
                        />
                      </div>
                    ) : isTrueFalse ? (
                      /* Enhanced True/False Question Display with Detailed Solution */
                      <div className="space-y-4">
                        <TrueFalseSolutionDisplay
                          question={question}
                          userAnswer={analysis.userAnswer}
                          analysis={analysis}
                        />
                      </div>
                    ) : isTLU ? (
                      /* TLU Question Display */
                      <div className="space-y-4">
                        <TLUSolutionDisplay
                          question={question}
                          userAnswer={analysis.userAnswer}
                          analysis={analysis}
                        />
                      </div>
                    ) : (
                      /* Enhanced Short Answer Question Display with Detailed Solution */
                      <div className="space-y-4">
                        <ShortAnswerSolutionDisplay
                          question={question}
                          userAnswer={analysis.userAnswer}
                          analysis={analysis}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default QuizSubmission
